import { Link } from "react-router-dom";
import type { Novel } from "@/lib/novel";
import { formatCount, formatWordCount } from "@/lib/novel";
import { useFavorites } from "@/hooks/useFavorites";

interface NovelCardProps {
  novel: Novel;
  rank?: number;
  showIntro?: boolean;
}

export default function NovelCard({ novel, rank, showIntro = true }: NovelCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(novel.id);
  const latest = novel.chapters[novel.chapters.length - 1];

  return (
    <div
      data-product-shop
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <Link to={`/detail/${novel.id}`} className="flex gap-4 p-3">
        <div className="relative h-40 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={novel.cover}
            alt={`${novel.title} 小说封面`}
            title={`${novel.title} 在线阅读 - 御书屋`}
            className="h-full w-full object-top"
          />
          {typeof rank === "number" && (
            <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-background-50">
              {rank}
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
              {novel.title}
            </h3>
            <button
              type="button"
              aria-label={favorite ? "取消收藏" : "收藏"}
              onClick={(event) => {
                event.preventDefault();
                toggleFavorite(novel.id);
              }}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors ${
                favorite
                  ? "bg-accent-100 text-accent-600"
                  : "bg-background-100 text-foreground-400 hover:text-accent-500"
              }`}
            >
              <i className={favorite ? "ri-bookmark-fill text-sm" : "ri-bookmark-line text-sm"}></i>
            </button>
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span>{novel.author}</span>
            <span className="h-1 w-1 rounded-full bg-background-400"></span>
            <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
              {novel.categoryName}
            </span>
            <span
              className={`rounded-sm px-1.5 py-0.5 ${
                novel.status === "完结"
                  ? "bg-primary-100 text-primary-700"
                  : "bg-accent-100 text-accent-700"
              }`}
            >
              {novel.status}
            </span>
          </p>
          {showIntro && (
            <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-foreground-600">
              {novel.intro}
            </p>
          )}
          <div className="mt-auto flex items-center justify-between pt-2 text-[11px] text-foreground-400">
            <span className="truncate">
              <i className="ri-file-list-3-line mr-1"></i>
              {latest ? latest.title : "暂无章节"}
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <span>{formatWordCount(novel.wordCount)}</span>
              <span>
                <i className="ri-fire-line mr-0.5"></i>
                {formatCount(novel.clicks)}
              </span>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}